import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import type { IChartApi } from 'lightweight-charts';
import { hitTestDrawings } from './hitTest';
import { renderDrawings } from './renderers';
import { normalizeDrawing } from './serialize';
import EditLineModal, { EditLineData } from './EditLineModal';
import NoteOverlayLayer from './NoteOverlayLayer';
import type { AnchorKey, DrawMode, Drawing, DrawingScope, DrawingStyle, DrawingVisibility } from './types';
import { noteHtmlToPlainText, sanitizeNoteHtml } from './noteHtml';
import { normalizeTimeSec } from './time';

// helper used by channel color updates; exported for tests
export function channelFillFromLine(color: string): string {
  if (typeof color !== 'string') return 'rgba(0,0,0,0.14)';
  const c = color.trim();
  // hex formats
  const hex6 = /^#([0-9a-fA-F]{6})$/;
  const hex3 = /^#([0-9a-fA-F]{3})$/;
  let r = 0, g = 0, b = 0;
  if (hex6.test(c)) {
    const m = c.match(hex6) as RegExpMatchArray;
    r = parseInt(m[1].slice(0, 2), 16);
    g = parseInt(m[1].slice(2, 4), 16);
    b = parseInt(m[1].slice(4, 6), 16);
  } else if (hex3.test(c)) {
    const m = c.match(hex3) as RegExpMatchArray;
    r = parseInt(m[1][0] + m[1][0], 16);
    g = parseInt(m[1][1] + m[1][1], 16);
    b = parseInt(m[1][2] + m[1][2], 16);
  } else {
    // try rgb(...) formats
    const rgbMatch = c.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
    if (rgbMatch) {
      r = Number(rgbMatch[1]);
      g = Number(rgbMatch[2]);
      b = Number(rgbMatch[3]);
    }
  }
  return `rgba(${r}, ${g}, ${b}, 0.14)`;
}

type Callbacks = {
  onCreate?: (drawing: Drawing) => void;
  onUpdate?: (drawing: Drawing) => void;
  onDelete?: (drawingId: string) => void;
  onInvalidPlacement?: (message: string) => void;
};

type ManagerInit = {
  chart: IChartApi;
  candleSeries: any;
  container: HTMLDivElement;
  symbol: string;
  tradeId?: string;
  scopeForNew: DrawingScope;
  visibility: DrawingVisibility;
  callbacks?: Callbacks;
};

type Point = { t: number; p: number; x: number; y: number };

function uid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const DEBUG_DRAWINGS = false;

export class DrawingManager {
  private chart: IChartApi;
  private candleSeries: any;
  private container: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private callbacks: Callbacks;
  private editingOverlay: HTMLDivElement | null = null;
  private noteOverlayHost: HTMLDivElement;
  private noteOverlayRoot: Root;
  private resizeObserver: ResizeObserver | null = null;

  private symbol = '';
  private tradeId?: string;
  private scopeForNew: DrawingScope = 'symbol';
  private visibility: DrawingVisibility = { symbol: true, trade: true };
  private locked = false;

  private mode: DrawMode = 'select';
  private drawings: Drawing[] = [];
  private selectedId: string | null = null;
  private noteEditingId: string | null = null;
  private noteDraftIds = new Set<string>();
  private noteStyleDebounceTimers = new Map<string, number>();
  private barTimes: number[] = [];
  private bars: any[] = [];
  private barsByTime: Map<number, any> = new Map();
  private snapToPoint: boolean = true;
  private snapTimePx: number = 8;
  private snapPricePx: number = 8;
  private invalidateRaf: number | null = null;
  private debugProjectionLoggedKeys = new Set<string>();

  // track current drag operation; start point allows us to move bodies
  private drag: { drawingId: string; anchor: AnchorKey; start?: Point } | null = null;
  private draftRectStart: Point | null = null;
  private draftRectCurrent: Point | null = null;
  private stagedPoints: Point[] = [];

  private isMouseDown = false;

  private readonly onMouseDownBound: (e: MouseEvent) => void;
  private readonly onMouseMoveBound: (e: MouseEvent) => void;
  private readonly onMouseUpBound: (e: MouseEvent) => void;
  private readonly onDoubleClickBound: (e: MouseEvent) => void;
  private readonly onKeyDownBound: (e: KeyboardEvent) => void;
  private readonly onResizeBound: () => void;
  private readonly onVisibleRangeBound: () => void;
  private readonly onSizeChangeBound: () => void;
  private readonly listenerOptions: AddEventListenerOptions;

  private unsubscribeChartEvents() {
    try { this.chart.timeScale().unsubscribeVisibleTimeRangeChange(this.onVisibleRangeBound); } catch {}
    try { this.chart.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleRangeBound); } catch {}
    try { (this.chart.timeScale() as any).unsubscribeSizeChange?.(this.onSizeChangeBound); } catch {}
  }

  private subscribeChartEvents() {
    try { this.chart.timeScale().subscribeVisibleTimeRangeChange(this.onVisibleRangeBound); } catch {}
    try { this.chart.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleRangeBound); } catch {}
    try { (this.chart.timeScale() as any).subscribeSizeChange?.(this.onSizeChangeBound); } catch {}
  }

  constructor(init: ManagerInit) {
    this.chart = init.chart;
    this.candleSeries = init.candleSeries;
    this.container = init.container;
    this.symbol = String(init.symbol || '').toUpperCase();
    this.tradeId = init.tradeId;
    this.scopeForNew = init.scopeForNew;
    this.visibility = init.visibility;
    this.callbacks = init.callbacks || {};

    this.canvas = document.createElement('canvas');
    this.canvas.style.position = 'absolute';
    this.canvas.style.inset = '0';
    this.canvas.style.pointerEvents = 'none';
    this.canvas.style.zIndex = '26';
    this.container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    this.noteOverlayHost = document.createElement('div');
    this.noteOverlayHost.style.position = 'absolute';
    this.noteOverlayHost.style.inset = '0';
    this.noteOverlayHost.style.pointerEvents = 'none';
    this.noteOverlayHost.style.zIndex = '27';
    this.container.appendChild(this.noteOverlayHost);
    this.noteOverlayRoot = createRoot(this.noteOverlayHost);

    this.onMouseDownBound = (e) => this.onMouseDown(e);
    this.onMouseMoveBound = (e) => this.onMouseMove(e);
    this.onMouseUpBound = (e) => this.onMouseUp(e);
    this.onDoubleClickBound = (e) => this.onDoubleClick(e);
    this.onKeyDownBound = (e) => this.onKeyDown(e);
    this.onResizeBound = () => this.invalidateOverlay();
    this.onVisibleRangeBound = () => this.invalidateOverlay();
    this.onSizeChangeBound = () => this.invalidateOverlay();
    this.listenerOptions = { capture: true };

    this.container.addEventListener('mousedown', this.onMouseDownBound, this.listenerOptions);
    this.container.addEventListener('mousemove', this.onMouseMoveBound, this.listenerOptions);
    this.container.addEventListener('dblclick', this.onDoubleClickBound, this.listenerOptions);
    window.addEventListener('mouseup', this.onMouseUpBound, this.listenerOptions);
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('resize', this.onResizeBound);

    this.subscribeChartEvents();

    try {
      this.resizeObserver = new ResizeObserver(() => this.invalidateOverlay());
      this.resizeObserver.observe(this.container);
    } catch {
      this.resizeObserver = null;
    }

    this.resizeCanvas();
    this.invalidateOverlay();
  }

  destroy() {
    this.container.removeEventListener('mousedown', this.onMouseDownBound, this.listenerOptions);
    this.container.removeEventListener('mousemove', this.onMouseMoveBound, this.listenerOptions);
    this.container.removeEventListener('dblclick', this.onDoubleClickBound, this.listenerOptions);
    window.removeEventListener('mouseup', this.onMouseUpBound, this.listenerOptions);
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('resize', this.onResizeBound);
    this.unsubscribeChartEvents();
    if (this.resizeObserver) {
      try { this.resizeObserver.disconnect(); } catch {}
      this.resizeObserver = null;
    }
    if (this.invalidateRaf != null) {
      try { window.cancelAnimationFrame(this.invalidateRaf); } catch {}
      this.invalidateRaf = null;
    }
    if (this.editingOverlay) {
      try { this.editingOverlay.remove(); } catch {}
      this.editingOverlay = null;
    }
    for (const timer of this.noteStyleDebounceTimers.values()) {
      window.clearTimeout(timer);
    }
    this.noteStyleDebounceTimers.clear();
    try { this.noteOverlayRoot.unmount(); } catch {}
    try { this.noteOverlayHost.remove(); } catch {}
    this.container.style.cursor = '';
    try { this.canvas.remove(); } catch {}
  }

  updateChartSeries(chart: IChartApi, candleSeries: any) {
    if (!chart || !candleSeries) return;
    const sameChart = this.chart === chart;
    this.candleSeries = candleSeries;
    if (!sameChart) {
      this.unsubscribeChartEvents();
      this.chart = chart;
      this.subscribeChartEvents();
    }
    this.invalidateOverlay();
  }

  setMode(mode: DrawMode) {
    this.mode = mode;
    this.cancelInProgress();
    this.invalidateOverlay();
  }

  setContext(symbol: string, tradeId?: string) {
    this.symbol = String(symbol || '').toUpperCase();
    this.tradeId = tradeId;
    this.invalidateOverlay();
  }

  setScopeForNew(scope: DrawingScope) {
    this.scopeForNew = scope;
  }

  setVisibility(visibility: DrawingVisibility) {
    this.visibility = visibility;
    this.invalidateOverlay();
  }

  setLocked(locked: boolean) {
    this.locked = Boolean(locked);
    if (this.locked) this.cancelInProgress();
    this.invalidateOverlay();
  }

  setDrawings(drawings: Drawing[]) {
    this.drawings = drawings.map((d) => normalizeDrawing(d));
    if (this.selectedId && !this.drawings.find((d) => d.id === this.selectedId)) {
      this.selectedId = null;
    }
    this.invalidateOverlay();
  }

  setBarTimes(times: Array<number | string | Date>) {
    const normalized = times
      .map((value) => normalizeTimeSec(value))
      .filter((value): value is number => Number.isFinite(Number(value)));
    normalized.sort((a, b) => a - b);
    const unique: number[] = [];
    for (const t of normalized) {
      if (!unique.length || unique[unique.length - 1] !== t) unique.push(t);
    }
    this.barTimes = unique;
    this.invalidateOverlay();
  }

  // accept raw displayed bars so snapping can use OHLC values when available
  setBars(bars: Array<any> | null | undefined) {
    const list = Array.isArray(bars) ? bars : [];
    this.bars = list.map((b) => ({ ...(b || {}) }));
    this.barsByTime.clear();
    for (const b of this.bars) {
      try {
        const t = normalizeTimeSec((b as any).time);
        if (Number.isFinite(Number(t))) this.barsByTime.set(Number(t), b);
      } catch {}
    }
  }

  setSnapToPoint(enabled: boolean, options?: { timePx?: number; pricePx?: number }) {
    this.snapToPoint = Boolean(enabled);
    if (options?.timePx != null) this.snapTimePx = Number(options.timePx) || this.snapTimePx;
    if (options?.pricePx != null) this.snapPricePx = Number(options.pricePx) || this.snapPricePx;
  }

  invalidateOverlay() {
    if (this.invalidateRaf != null) return;
    this.invalidateRaf = window.requestAnimationFrame(() => {
      this.invalidateRaf = null;
      this.render();
    });
  }

  private getVisibleDrawings() {
    return this.drawings.filter((d) => {
      if (d.scope === 'symbol' && !this.visibility.symbol) return false;
      if (d.scope === 'trade' && !this.visibility.trade) return false;
      return true;
    });
  }

  private resizeCanvas() {
    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    this.canvas.width = Math.floor(w * dpr);
    this.canvas.height = Math.floor(h * dpr);
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private nearestBarTime(tSec: number): number {
    if (!this.barTimes.length) return tSec;
    let left = 0;
    let right = this.barTimes.length - 1;
    if (tSec <= this.barTimes[left]) return this.barTimes[left];
    if (tSec >= this.barTimes[right]) return this.barTimes[right];

    while (left <= right) {
      const mid = (left + right) >> 1;
      const mt = this.barTimes[mid];
      if (mt === tSec) return mt;
      if (mt < tSec) left = mid + 1;
      else right = mid - 1;
    }

    const lo = Math.max(0, right);
    const hi = Math.min(this.barTimes.length - 1, left);
    const loDiff = Math.abs(this.barTimes[lo] - tSec);
    const hiDiff = Math.abs(this.barTimes[hi] - tSec);
    return loDiff <= hiDiff ? this.barTimes[lo] : this.barTimes[hi];
  }

  private findBarTimeBracketIndex(tSec: number): number {
    if (this.barTimes.length < 2) return -1;
    if (tSec < this.barTimes[0]) return -1;
    if (tSec > this.barTimes[this.barTimes.length - 1]) return -1;

    let left = 0;
    let right = this.barTimes.length - 2;
    while (left <= right) {
      const mid = (left + right) >> 1;
      const tLeft = this.barTimes[mid];
      const tRight = this.barTimes[mid + 1];
      if (tSec < tLeft) {
        right = mid - 1;
      } else if (tSec > tRight) {
        left = mid + 1;
      } else {
        return mid;
      }
    }

    return -1;
  }

  private isExactBarTime(tSec: number): boolean {
    if (!this.barTimes.length) return false;
    let left = 0;
    let right = this.barTimes.length - 1;
    while (left <= right) {
      const mid = (left + right) >> 1;
      const mt = this.barTimes[mid];
      if (mt === tSec) return true;
      if (mt < tSec) left = mid + 1;
      else right = mid - 1;
    }
    return false;
  }

  private projectTimeToX(value: unknown): number | null {
    const t = normalizeTimeSec(value);
    if (t == null) return null;

    const clampToContainer = (x: number) => {
      const w = this.container?.clientWidth || 0;
      // always prevent coordinates from going negative; that's what caused
      // points to wander off left when the chart briefly reported width===0.
      if (x < 0) return 0;
      // only enforce a right-hand limit if we actually know the width
      if (w > 0 && x > w) return w;
      return x;
    };

    try {
      const direct = this.chart.timeScale().timeToCoordinate(t as any);
      const directFinite = Number.isFinite(Number(direct));

      if (!this.barTimes.length) {
        return directFinite ? clampToContainer(Number(direct)) : null;
      }

      const first = this.barTimes[0];
      const last = this.barTimes[this.barTimes.length - 1];

      // direct projection can be used for exact timestamps, but for non-exact
      // in-range values we prefer interpolation to avoid implicit snapping.
      const exactBar = this.isExactBarTime(t);
      if (directFinite && exactBar) {
        return clampToContainer(Number(direct));
      }

      // 2) out of known range: keep existing edge pin behavior
      if (t < first) {
        const firstX = this.chart.timeScale().timeToCoordinate(first as any);
        if (Number.isFinite(Number(firstX))) return clampToContainer(Number(firstX));
        return directFinite ? clampToContainer(Number(direct)) : null;
      }
      if (t > last) {
        const lastX = this.chart.timeScale().timeToCoordinate(last as any);
        if (Number.isFinite(Number(lastX))) return clampToContainer(Number(lastX));
        return directFinite ? clampToContainer(Number(direct)) : null;
      }

      // 3) in-range interpolation between surrounding bars (true timestamp anchoring)
      const i = this.findBarTimeBracketIndex(t);
      if (i < 0) return directFinite ? clampToContainer(Number(direct)) : null;

      const tL = this.barTimes[i];
      const tR = this.barTimes[i + 1];
      if (tR === tL) {
        const xSame = this.chart.timeScale().timeToCoordinate(tL as any);
        if (Number.isFinite(Number(xSame))) return clampToContainer(Number(xSame));
        return null;
      }

      const xLRaw = this.chart.timeScale().timeToCoordinate(tL as any);
      const xRRaw = this.chart.timeScale().timeToCoordinate(tR as any);
      const xL = Number(xLRaw);
      const xR = Number(xRRaw);

      if (Number.isFinite(xL) && Number.isFinite(xR)) {
        const frac = (t - tL) / (tR - tL);
        return clampToContainer(xL + frac * (xR - xL));
      }

      // last-resort fallback if one side coordinate is unavailable
      if (directFinite) return clampToContainer(Number(direct));
      const snapped = this.nearestBarTime(t);
      const snappedX = this.chart.timeScale().timeToCoordinate(snapped as any);
      return Number.isFinite(Number(snappedX)) ? clampToContainer(Number(snappedX)) : null;
    } catch {
      return null;
    }
  }

  /**
   * project a price to pixel Y; if outside visible range clamp it to edge
   * of current scale so drawing remains visible instead of disappearing.
   */
  private projectPriceToY(price: number): number | null {
    const p = Number(price);
    if (!Number.isFinite(p)) return null;

    const debug = {
      price: p,
      directY: null as number | null,
      visibleRange: null as { from: number; to: number } | null,
      topPrice: null as number | null,
      bottomPrice: null as number | null,
      returnedY: null as number | null,
      path: 'none' as 'direct' | 'clamped' | 'edge-infer' | 'none',
    };

    try {
      const raw = this.candleSeries?.priceToCoordinate?.(p);
      if (raw != null && Number.isFinite(Number(raw))) {
        const y = Number(raw);
        debug.directY = y;
        debug.returnedY = y;
        debug.path = 'direct';
        return y;
      }
    } catch {}

    // fallback: clamp using visible price range
    try {
      const candleScale = this.candleSeries?.priceScale?.();
      const fallbackScale = this.chart.priceScale('right');
      const vr: any = candleScale?.getVisibleRange?.() ?? fallbackScale?.getVisibleRange?.();
      if (vr && Number.isFinite(Number(vr.from)) && Number.isFinite(Number(vr.to))) {
        const from = Number(vr.from);
        const to = Number(vr.to);
        const lo = Math.min(from, to);
        const hi = Math.max(from, to);
        debug.visibleRange = { from, to };
        const clamped = Math.min(Math.max(p, lo), hi);
        try {
          const y2 = this.candleSeries?.priceToCoordinate?.(clamped);
          if (y2 != null && Number.isFinite(Number(y2))) {
            const y = Number(y2);
            debug.returnedY = y;
            debug.path = 'clamped';
            return y;
          }
        } catch {}
      }
    } catch {}

    // fallback: infer viewport edges using coordinate->price
    try {
      const height = Math.max(1, Number(this.container?.clientHeight || 0));
      const top = this.candleSeries?.coordinateToPrice?.(0);
      const bottom = this.candleSeries?.coordinateToPrice?.(Math.max(0, height - 1));
      if (Number.isFinite(Number(top)) && Number.isFinite(Number(bottom))) {
        const topPrice = Number(top);
        const bottomPrice = Number(bottom);
        debug.topPrice = topPrice;
        debug.bottomPrice = bottomPrice;
        const lo = Math.min(topPrice, bottomPrice);
        const hi = Math.max(topPrice, bottomPrice);

        if (p < lo) {
          debug.returnedY = Math.max(0, height - 1);
          debug.path = 'edge-infer';
          return Math.max(0, height - 1);
        }
        if (p > hi) {
          debug.returnedY = 0;
          debug.path = 'edge-infer';
          return 0;
        }
      }
    } catch {}

    if (DEBUG_DRAWINGS) {
      try {
        const key = `${Math.round(p * 10000) / 10000}:${debug.path}:${debug.returnedY ?? 'null'}`;
        if (!this.debugProjectionLoggedKeys.has(key)) {
          this.debugProjectionLoggedKeys.add(key);
          console.debug('[DrawingManager] projectPriceToY fallback', debug);
        }
      } catch {}
    }

    return null;
  }

  private isTypingContext(): boolean {
    try {
      const active = document.activeElement as HTMLElement | null;
      if (!active) return false;
      if (active.matches('input,textarea,select')) return true;
      if (active.isContentEditable) return true;
      if (active.closest('[contenteditable="true"]')) return true;
      return false;
    } catch {
      return false;
    }
  }

  private isNoteOverlayUiEvent(e: MouseEvent): boolean {
    try {
      const target = e.target as HTMLElement | null;
      if (!target) return false;
      return Boolean(target.closest('[data-drawing-note-ui="true"]'));
    } catch {
      return false;
    }
  }

  private projectors() {
    let axisRight = 40;
    try {
      axisRight = this.chart.priceScale('right')?.width() ?? axisRight;
    } catch {}
    return {
      width: this.container.clientWidth,
      height: this.container.clientHeight,
      axisRightWidth: axisRight,
      timeToX: (t: number) => {
        return this.projectTimeToX(t);
      },
      priceToY: (p: number) => {
        try {
          return this.projectPriceToY(Number(p));
        } catch {
          return null;
        }
      },
      xToTime: (x: number) => {
        try {
          const t = this.chart.timeScale().coordinateToTime(x as any);
          const n = normalizeTimeSec(t);
          if (n != null) return this.nearestBarTime(n);

          const visible = this.chart.timeScale().getVisibleRange?.();
          const from = normalizeTimeSec((visible as any)?.from);
          const to = normalizeTimeSec((visible as any)?.to);
          if (from == null || to == null) return null;

          const leftRaw = this.chart.timeScale().timeToCoordinate((visible as any).from);
          const rightRaw = this.chart.timeScale().timeToCoordinate((visible as any).to);
          const left = Number(leftRaw);
          const right = Number(rightRaw);
          if (!Number.isFinite(left) || !Number.isFinite(right) || right === left) return null;

          const ratio = (x - left) / (right - left);
          const interpolated = from + (to - from) * ratio;
          if (!Number.isFinite(interpolated)) return null;
          return this.nearestBarTime(interpolated);
        } catch {
          return null;
        }
      },
      yToPrice: (y: number) => {
        try {
          const p = this.candleSeries?.coordinateToPrice?.(y);
          if (Number.isFinite(Number(p))) return Number(p);

          // Fallback: interpolate between top and bottom chart prices
          try {
            const height = this.container.clientHeight || 1;
            const topPrice = this.candleSeries?.coordinateToPrice?.(0);
            const bottomPrice = this.candleSeries?.coordinateToPrice?.(height);
            if (Number.isFinite(Number(topPrice)) && Number.isFinite(Number(bottomPrice))) {
              const tp = Number(topPrice);
              const bp = Number(bottomPrice);
              const ratio = Math.max(0, Math.min(1, y / height));
              return tp + (bp - tp) * ratio;
            }
          } catch {}
          return null;
        } catch {
          return null;
        }
      },
    };
  }

  private render() {
    if (!this.ctx) return;
    this.resizeCanvas();
    const projectors = this.projectors();
    renderDrawings(this.ctx, this.getVisibleDrawings(), this.selectedId, projectors);
    this.renderNoteOverlay(projectors);

    if (this.mode === 'rect' && this.draftRectStart && this.draftRectCurrent) {
      const x1 = this.draftRectStart.x;
      const y1 = this.draftRectStart.y;
      const x2 = this.draftRectCurrent.x;
      const y2 = this.draftRectCurrent.y;
      this.ctx.save();
      this.ctx.strokeStyle = '#3B82F6';
      this.ctx.fillStyle = 'rgba(59,130,246,0.16)';
      this.ctx.lineWidth = 2;
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      this.ctx.fillRect(left, top, right - left, bottom - top);
      this.ctx.strokeRect(left, top, right - left, bottom - top);
      this.ctx.restore();
    }

    if ((this.mode === 'trend' || this.mode === 'ray' || this.mode === 'channel') && this.stagedPoints.length > 0) {
      const p0 = this.stagedPoints[0];
      this.ctx.save();
      this.ctx.strokeStyle = '#3B82F6';
      this.ctx.lineWidth = 2;
      this.ctx.beginPath();
      this.ctx.arc(p0.x, p0.y, 4, 0, Math.PI * 2);
      this.ctx.fillStyle = '#3B82F6';
      this.ctx.fill();
      this.ctx.restore();
    }
  }

  private renderNoteOverlay(projectors: ReturnType<DrawingManager['projectors']>) {
    const notes = this.getVisibleDrawings()
      .filter((d) => d.tool === 'note')
      .map((drawing) => {
        const anchors = drawing.anchors as any;
        const t = normalizeTimeSec(anchors?.t);
        const p = Number(anchors?.p);
        const x = t == null ? null : projectors.timeToX(t);
        const y = projectors.priceToY(p);
        const dx = Number.isFinite(Number(anchors?.dx)) ? Number(anchors.dx) : 12;
        const dy = Number.isFinite(Number(anchors?.dy)) ? Number(anchors.dy) : -12;
        if (x == null || y == null) {
          return {
            drawing,
            anchorX: -9999,
            anchorY: -9999,
            boxX: -9999,
            boxY: -9999,
            visible: false,
          };
        }

        const boxX = x + dx;
        const boxY = y + dy;
        const margin = 320;
        const visible = boxX >= -margin && boxX <= projectors.width + margin && boxY >= -margin && boxY <= projectors.height + margin;
        return {
          drawing,
          anchorX: x,
          anchorY: y,
          boxX,
          boxY,
          visible,
        };
      });

    this.noteOverlayRoot.render(
      React.createElement(NoteOverlayLayer, {
        notes,
        width: projectors.width,
        height: projectors.height,
        selectedId: this.selectedId,
        editingId: this.noteEditingId,
        onSelect: (id: string) => {
          this.selectedId = id;
          this.render();
        },
        onBeginEdit: (id: string) => {
          this.selectedId = id;
          this.noteEditingId = id;
          this.render();
        },
        onCommitEdit: (id: string, text: string) => {
          this.commitNoteEdit(id, text);
        },
        onCancelEdit: (id: string) => {
          this.cancelNoteEdit(id);
        },
        onDelete: (id: string) => {
          this.deleteNote(id);
        },
        onDragOffset: (id: string, dx: number, dy: number, commit: boolean) => {
          this.updateNoteOffset(id, dx, dy, commit);
        },
        onStyleChange: (id: string, nextStyle: Partial<NonNullable<Drawing['style']>>) => {
          this.updateNoteStyle(id, nextStyle);
        },
      }),
    );
  }

  private commitNoteEdit(id: string, html: string) {
    const idx = this.drawings.findIndex((d) => d.id === id && d.tool === 'note');
    if (idx < 0) return;
    const sanitizedHtml = sanitizeNoteHtml(String(html || ''));
    const plain = noteHtmlToPlainText(sanitizedHtml).trim();
    const isDraft = this.noteDraftIds.has(id);

    if (!plain) {
      this.drawings.splice(idx, 1);
      this.noteDraftIds.delete(id);
      this.noteEditingId = null;
      if (!isDraft) this.callbacks.onDelete?.(id);
      if (this.selectedId === id) this.selectedId = null;
      this.invalidateOverlay();
      return;
    }

    const updated = normalizeDrawing({
      ...this.drawings[idx],
      text: plain,
      textHtml: sanitizedHtml,
      style: {
        ...(this.drawings[idx].style || {}),
        fontSize: Number(this.drawings[idx].style?.fontSize ?? 14),
        fontColor: String(this.drawings[idx].style?.fontColor || '#E5E7EB'),
      },
    });

    this.drawings[idx] = updated;
    this.noteEditingId = null;
    this.selectedId = updated.id;
    if (isDraft) {
      this.noteDraftIds.delete(updated.id);
      this.callbacks.onCreate?.(updated);
    } else {
      this.callbacks.onUpdate?.(updated);
    }
    this.invalidateOverlay();
  }

  private cancelNoteEdit(id: string) {
    const idx = this.drawings.findIndex((d) => d.id === id && d.tool === 'note');
    if (idx < 0) return;
    const isDraft = this.noteDraftIds.has(id);
    if (isDraft) {
      this.drawings.splice(idx, 1);
      this.noteDraftIds.delete(id);
      if (this.selectedId === id) this.selectedId = null;
    }
    this.noteEditingId = null;
    this.invalidateOverlay();
  }

  private deleteNote(id: string) {
    const idx = this.drawings.findIndex((d) => d.id === id && d.tool === 'note');
    if (idx < 0) return;
    const isDraft = this.noteDraftIds.has(id);
    this.drawings.splice(idx, 1);
    this.noteDraftIds.delete(id);
    this.noteEditingId = null;
    if (this.selectedId === id) this.selectedId = null;
    if (!isDraft) this.callbacks.onDelete?.(id);
    this.invalidateOverlay();
  }

  private updateNoteOffset(id: string, dx: number, dy: number, commit: boolean) {
    const idx = this.drawings.findIndex((d) => d.id === id && d.tool === 'note');
    if (idx < 0) return;
    const current = this.drawings[idx];
    const updated = normalizeDrawing({
      ...current,
      anchors: {
        ...(current.anchors as any),
        dx,
        dy,
      },
    });
    this.drawings[idx] = updated;
    this.selectedId = id;
    this.invalidateOverlay();

    if (commit && !this.noteDraftIds.has(id)) {
      this.callbacks.onUpdate?.(updated);
    }
  }

  private updateNoteStyle(id: string, nextStyle: Partial<NonNullable<Drawing['style']>>) {
    const idx = this.drawings.findIndex((d) => d.id === id && d.tool === 'note');
    if (idx < 0) return;
    const current = this.drawings[idx];
    const updated = normalizeDrawing({
      ...current,
      style: {
        ...(current.style || {}),
        ...nextStyle,
      },
    });
    this.drawings[idx] = updated;
    this.invalidateOverlay();

    if (this.noteDraftIds.has(id)) return;
    const existingTimer = this.noteStyleDebounceTimers.get(id);
    if (existingTimer != null) {
      window.clearTimeout(existingTimer);
    }
    const timer = window.setTimeout(() => {
      this.noteStyleDebounceTimers.delete(id);
      const latestIdx = this.drawings.findIndex((d) => d.id === id && d.tool === 'note');
      if (latestIdx >= 0) this.callbacks.onUpdate?.(this.drawings[latestIdx]);
    }, 300);
    this.noteStyleDebounceTimers.set(id, timer);
  }

  private eventPoint(e: MouseEvent): Point | null {
    const rect = this.container.getBoundingClientRect();
    let x = e.clientX - rect.left;
    let y = e.clientY - rect.top;
    const p = this.projectors();
    let t = p.xToTime(x);
    if (t == null) return null;
    let price = p.yToPrice(y);
    // if coordinateToPrice failed, try to still return a point using interpolation fallback
    if (price == null) {
      try {
        const height = this.container.clientHeight || 1;
        const topPrice = this.candleSeries?.coordinateToPrice?.(0);
        const bottomPrice = this.candleSeries?.coordinateToPrice?.(height);
        if (Number.isFinite(Number(topPrice)) && Number.isFinite(Number(bottomPrice))) {
          const ratio = Math.max(0, Math.min(1, y / height));
          price = Number(topPrice) + (Number(bottomPrice) - Number(topPrice)) * ratio;
        }
      } catch {}
    }
    if (price == null) return null;

    // snapping behavior: if enabled, snap to nearest bar time (X) and/or OHLC price (Y)
    if (this.snapToPoint) {
      try {
        const snapTimePx = Number(this.snapTimePx) || 0;
        const snapPricePx = Number(this.snapPricePx) || 0;

        // try snapping time -> nearest bar
        const nearestT = this.nearestBarTime(t);
        if (Number.isFinite(Number(nearestT))) {
          try {
            const barXRaw = this.chart.timeScale().timeToCoordinate(nearestT as any);
            const barX = Number(barXRaw);
            if (Number.isFinite(barX) && Math.abs(x - barX) <= snapTimePx) {
              x = barX;
              t = nearestT;
            }
          } catch {}
        }

        // try snapping price -> OHLC for the nearest bar (if we have bar data)
        let bar = this.barsByTime.get(Math.round(Number(t)));
        if (!bar && this.bars && this.bars.length) {
          // fallback: find closest bar by time distance
          let best: any = null;
          let bestDiff = Infinity;
          for (const b of this.bars) {
            try {
              const bt = normalizeTimeSec((b as any).time);
              if (!Number.isFinite(Number(bt))) continue;
              const diff = Math.abs(Number(bt) - Number(t));
              if (diff < bestDiff) {
                bestDiff = diff;
                best = b;
              }
            } catch {}
          }
          if (best) bar = best;
        }

        if (bar) {
          const candidates: number[] = [];
          try { if (Number.isFinite(Number((bar as any).open))) candidates.push(Number((bar as any).open)); } catch {}
          try { if (Number.isFinite(Number((bar as any).high))) candidates.push(Number((bar as any).high)); } catch {}
          try { if (Number.isFinite(Number((bar as any).low))) candidates.push(Number((bar as any).low)); } catch {}
          try { if (Number.isFinite(Number((bar as any).close))) candidates.push(Number((bar as any).close)); } catch {}

          let bestY: number | null = null;
          let bestPriceVal: number | null = null;
          let bestDy = Infinity;
          for (const cand of candidates) {
            try {
              const cyRaw = this.candleSeries?.priceToCoordinate?.(cand);
              const cy = Number(cyRaw);
              if (!Number.isFinite(cy)) continue;
              const dy = Math.abs(y - cy);
              if (dy < bestDy) {
                bestDy = dy;
                bestY = cy;
                bestPriceVal = cand;
              }
            } catch {}
          }
          if (bestY != null && bestPriceVal != null && bestDy <= snapPricePx) {
            y = bestY;
            price = bestPriceVal;
          }
        }
      } catch {}
    }

    return { t, p: price, x, y };
  }

  private isEventInsideContainer(e: MouseEvent): boolean {
    const rect = this.container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    return x >= 0 && x <= rect.width && y >= 0 && y <= rect.height;
  }

  private defaultStyle(tool: DrawMode): DrawingStyle {
    if (tool === 'note') {
      return {
        lineWidth: 1,
        lineStyle: 'solid',
        lineColor: '#E5E7EB',
        opacity: 1,
        fontSize: 14,
        fontColor: '#E5E7EB',
        bgColor: '#222226',
        padding: 8,
      };
    }
    if (tool === 'hline') {
      // default: thin solid purple line
      return { lineWidth: 1, lineStyle: 'solid', lineColor: '#8B5CF6', opacity: 1 };
    }
    if (tool === 'rect') {
      return { lineWidth: 2, lineStyle: 'solid', lineColor: '#3B82F6', fillColor: 'rgba(59,130,246,0.18)', opacity: 1 };
    }
    if (tool === 'channel') {
      return { lineWidth: 2, lineStyle: 'solid', lineColor: '#10B981', fillColor: 'rgba(16,185,129,0.14)', opacity: 1 };
    }
    if (tool === 'vline') {
      // default: thin solid purple line (match horizontal)
      return { lineWidth: 1, lineStyle: 'solid', lineColor: '#8B5CF6', opacity: 1 };
    }
    return { lineWidth: 2, lineStyle: 'solid', lineColor: '#3B82F6', opacity: 1 };
  }

  private createDrawing(
    tool: Exclude<DrawMode, 'select'>,
    anchors: any,
    extras?: Partial<Drawing>,
    persist = true,
  ) {
    const style = this.defaultStyle(tool);
    if (tool !== 'note') {
      style.label = this.computeNextLabelName();
    }
    const d = normalizeDrawing({
      id: uid(),
      scope: this.scopeForNew,
      symbol: this.symbol,
      tradeId: this.scopeForNew === 'trade' ? this.tradeId : undefined,
      tool,
      anchors,
      style,
      ...extras,
    });
    this.selectedId = d.id;
    this.drawings = [...this.drawings.filter((x) => x.id !== d.id), d];
    if (persist) this.callbacks.onCreate?.(d);
    this.render();
    return d;
  }

  private computeNextLabelName(): string {
    const re = /^Label\s+(\d+)$/i;
    let max = 0;
    for (const d of this.drawings) {
      try {
        const lab = String(d.style?.label || '');
        const m = lab.match(re);
        if (m) {
          const n = Number(m[1]);
          if (Number.isFinite(n) && n > max) max = n;
        }
      } catch {}
    }
    return `Label ${max + 1}`;
  }

  private onMouseDown(e: MouseEvent) {
    if (e.button !== 0) return;
    if (this.isNoteOverlayUiEvent(e)) return;
    if (this.locked) return;
    if (!(this.mode === 'select' || this.mode === 'rect')) return;
    const pt = this.eventPoint(e);
    if (!pt) return;
    this.isMouseDown = true;

    if (this.mode === 'select') {
      const p = this.projectors();
      const labelHitId = this.findLineLabelHit(pt.x, pt.y, p);
      if (labelHitId) {
        this.selectedId = labelHitId;
        e.preventDefault();
        e.stopPropagation();
        this.drag = { drawingId: labelHitId, anchor: 'body', start: pt };
        this.render();
        return;
      }
      const hit = hitTestDrawings(this.getVisibleDrawings(), pt.x, pt.y, {
        timeToX: p.timeToX,
        priceToY: p.priceToY,
        width: p.width,
      });
      this.selectedId = hit.drawingId;
      if (hit.drawingId && hit.anchor !== 'none') {
        e.preventDefault();
        e.stopPropagation();
        this.drag = { drawingId: hit.drawingId, anchor: hit.anchor, start: pt };
      }
      this.render();
      return;
    }

    if (this.mode === 'rect') {
      e.preventDefault();
      e.stopPropagation();
      this.draftRectStart = pt;
      this.draftRectCurrent = pt;
      this.render();
    }
  }

  private onMouseMove(e: MouseEvent) {
    if (this.locked) return;
    const pt = this.eventPoint(e);

    if (!pt) return;

    if (this.mode === 'select') {
      const p = this.projectors();
      const labelHitId = this.findLineLabelHit(pt.x, pt.y, p);
      this.container.style.cursor = labelHitId ? 'default' : '';
    }

    if (this.mode === 'rect' && this.isMouseDown && this.draftRectStart) {
      e.preventDefault();
      e.stopPropagation();
      this.draftRectCurrent = pt;
      this.render();
      return;
    }

    if (this.mode === 'select' && this.drag) {
      e.preventDefault();
      e.stopPropagation();
      const idx = this.drawings.findIndex((d) => d.id === this.drag?.drawingId);
      if (idx < 0) return;
      const original = this.drawings[idx];
      const next = normalizeDrawing({ ...original });

      if (next.tool === 'vline') {
        (next.anchors as any).t = pt.t;
      } else if (next.tool === 'hline') {
        (next.anchors as any).p = pt.p;
      } else {
        // no special-case handling for vline/hline here
      }

      if (this.drag.anchor === 'body' && this.drag?.start) {
        // translate whole shapes when dragging the body for applicable tools
        if (next.tool === 'rect') {
          // translate both corners by delta since we are moving the rectangle
          const dx = pt.t - this.drag.start.t;
          const dy = pt.p - this.drag.start.p;
          (next.anchors as any).t1 += dx;
          (next.anchors as any).p1 += dy;
          (next.anchors as any).t2 += dx;
          (next.anchors as any).p2 += dy;
          // update start for incremental moves
          this.drag.start = pt;
        } else if (next.tool === 'trend' || next.tool === 'ray') {
          const dx = pt.t - this.drag.start.t;
          const dy = pt.p - this.drag.start.p;
          (next.anchors as any).t1 += dx;
          (next.anchors as any).p1 += dy;
          (next.anchors as any).t2 += dx;
          (next.anchors as any).p2 += dy;
          this.drag.start = pt;
        } else if (next.tool === 'channel') {
          const dx = pt.t - this.drag.start.t;
          const dy = pt.p - this.drag.start.p;
          (next.anchors as any).t1 += dx;
          (next.anchors as any).p1 += dy;
          (next.anchors as any).t2 += dx;
          (next.anchors as any).p2 += dy;
          (next.anchors as any).t3 += dx;
          (next.anchors as any).p3 += dy;
          this.drag.start = pt;
        }
      } else if (this.drag.anchor === 'p1') {
        (next.anchors as any).t1 = pt.t;
        (next.anchors as any).p1 = pt.p;
      } else if (this.drag.anchor === 'p2') {
        (next.anchors as any).t2 = pt.t;
        (next.anchors as any).p2 = pt.p;
      } else if (this.drag.anchor === 'p3' && next.tool === 'channel') {
        (next.anchors as any).t3 = pt.t;
        (next.anchors as any).p3 = pt.p;
      }

      this.drawings[idx] = next;
      this.render();
    }
  }

  private onMouseUp(e: MouseEvent) {
    if (this.locked) {
      this.isMouseDown = false;
      this.drag = null;
      return;
    }

    const clickedInside = this.isEventInsideContainer(e);
    if ((this.mode === 'trend' || this.mode === 'ray' || this.mode === 'channel' || this.mode === 'vline' || this.mode === 'hline' || this.mode === 'note') && !clickedInside) {
      this.isMouseDown = false;
      return;
    }

    const pt = this.eventPoint(e);

    if (this.mode === 'rect' && this.draftRectStart && this.draftRectCurrent && pt) {
      e.preventDefault();
      e.stopPropagation();
      const s = this.draftRectStart;
      const c = this.draftRectCurrent;
      this.createDrawing('rect', { t1: s.t, p1: s.p, t2: c.t, p2: c.p });
      this.draftRectStart = null;
      this.draftRectCurrent = null;
      this.mode = 'select';
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'trend' && pt) {
      this.stagedPoints.push(pt);
      if (this.stagedPoints.length >= 2) {
        const [a, b] = this.stagedPoints;
        this.createDrawing('trend', { t1: a.t, p1: a.p, t2: b.t, p2: b.p });
        this.stagedPoints = [];
        this.mode = 'select';
      }
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'ray' && pt) {
      this.stagedPoints.push(pt);
      if (this.stagedPoints.length >= 2) {
        const [a, b] = this.stagedPoints;
        this.createDrawing('ray', { t1: a.t, p1: a.p, t2: b.t, p2: b.p });
        this.stagedPoints = [];
        this.mode = 'select';
      }
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'channel' && pt) {
      this.stagedPoints.push(pt);
      if (this.stagedPoints.length >= 3) {
        const [a, b, c] = this.stagedPoints;
        this.createDrawing('channel', { t1: a.t, p1: a.p, t2: b.t, p2: b.p, t3: c.t, p3: c.p });
        this.stagedPoints = [];
        this.mode = 'select';
      }
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'hline' && pt) {
      this.createDrawing('hline', { p: pt.p });
      this.mode = 'select';
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'vline' && pt) {
      this.createDrawing('vline', { t: pt.t });
      this.mode = 'select';
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'note' && pt) {
      const created = this.createDrawing(
        'note',
        { t: pt.t, p: pt.p, dx: 12, dy: -12 },
        { text: '' },
        false,
      );
      this.noteDraftIds.add(created.id);
      this.noteEditingId = created.id;
      this.mode = 'select';
      this.render();
      this.isMouseDown = false;
      return;
    }

    if (this.mode === 'select' && this.drag) {
      e.preventDefault();
      e.stopPropagation();
      const idx = this.drawings.findIndex((d) => d.id === this.drag?.drawingId);
      if (idx >= 0) {
        this.callbacks.onUpdate?.(this.drawings[idx]);
      }
      this.drag = null;
      this.render();
    }

    this.isMouseDown = false;
  }

  private cancelInProgress() {
    this.stagedPoints = [];
    this.draftRectStart = null;
    this.draftRectCurrent = null;
    this.drag = null;
  }

  private onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (this.noteEditingId) {
        this.cancelNoteEdit(this.noteEditingId);
      }
      this.cancelInProgress();
      this.mode = 'select';
      this.render();
      return;
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedId && !this.locked) {
      if (this.noteEditingId != null) return;
      if (this.isTypingContext()) return;
      const id = this.selectedId;
      this.selectedId = null;
      this.callbacks.onDelete?.(id);
      this.render();
    }
  }

  private async onDoubleClick(e: MouseEvent) {
    if (this.locked) return;
    const pt = this.eventPoint(e);
    if (!pt) return;

    const p = this.projectors();
    const hit = hitTestDrawings(this.getVisibleDrawings(), pt.x, pt.y, {
      timeToX: p.timeToX,
      priceToY: p.priceToY,
      width: p.width,
    });
    const drawingId = hit.drawingId || this.findLineLabelHit(pt.x, pt.y, p);
    if (!drawingId) return;

    const idx = this.drawings.findIndex((d) => d.id === drawingId);
    if (idx < 0) return;
    const drawing = this.drawings[idx];
    if (drawing.tool === 'vline' || drawing.tool === 'hline' || drawing.tool === 'rect' || drawing.tool === 'trend' || drawing.tool === 'ray' || drawing.tool === 'channel') {
      e.preventDefault();
      e.stopPropagation();

        const hideLabelFor = ['rect', 'trend', 'ray', 'channel'];
        const showLabel = !hideLabelFor.includes(drawing.tool);
        const edited = await this.openLineEditDialogReact({
          label: String(drawing.style?.label || '').trim(),
          lineColor: String(drawing.style?.lineColor || '#F59E0B'),
          lineStyle: drawing.style?.lineStyle || 'solid',
          lineWidth: Number(drawing.style?.lineWidth ?? 2),
          showLabel,
          showPrice: drawing.tool === 'hline',
          price: drawing.tool === 'hline' ? Number((drawing.anchors as any)?.p) : undefined,
        });
      if (!edited) return;

      const nextAnchors = { ...(drawing.anchors as any) };
      if (drawing.tool === 'hline' && Number.isFinite(Number(edited.price))) {
        nextAnchors.p = Number(edited.price);
      }

        const updated = normalizeDrawing({
          ...drawing,
          anchors: nextAnchors,
          style: {
            ...(drawing.style || {}),
            label: showLabel ? (String(edited.label).trim() || undefined) : drawing.style?.label,
            lineColor: String(edited.lineColor).trim() || undefined,
            lineStyle: edited.lineStyle,
            lineWidth: edited.lineWidth,
            // for channels adjust the fill to a translucent version of the line color
            ...(drawing.tool === 'channel'
              ? { fillColor: channelFillFromLine(String(edited.lineColor).trim() || (drawing.style?.lineColor as string) || '#000000') }
              : {}),
          },
        });
      this.drawings[idx] = updated;
      this.selectedId = updated.id;
      this.callbacks.onUpdate?.(updated);
      this.render();
    }
  }

  private findLineLabelHit(
    x: number,
    y: number,
    projectors: ReturnType<DrawingManager['projectors']>,
  ): string | null {
    if (!this.ctx) return null;
    for (let i = this.getVisibleDrawings().length - 1; i >= 0; i -= 1) {
      const drawing = this.getVisibleDrawings()[i];
      const style = drawing.style || {};
      if (drawing.tool === 'hline') {
        const p = Number((drawing.anchors as any).p);
        const yLine = projectors.priceToY(p);
        if (yLine == null) continue;
        const label = String(style.label || '').trim();
        const priceLabel = p.toFixed(2);
        this.ctx.save();
        this.ctx.font = '12px sans-serif';
        const labelMetrics = label ? this.ctx.measureText(label) : { width: 0 };
        const priceMetrics = this.ctx.measureText(priceLabel);
        this.ctx.restore();
        const { by, boxH, hasLabel, label: labelBox, price: priceBox } = this.getHLineBoxes(projectors, yLine, labelMetrics.width, priceMetrics.width);
        const hitLabel = hasLabel && x >= labelBox.bx && x <= labelBox.bx + labelBox.boxW && y >= by && y <= by + boxH;
        const hitPrice = x >= priceBox.bx && x <= priceBox.bx + priceBox.boxW && y >= by && y <= by + boxH;
        if (hitLabel || hitPrice) return drawing.id;
      }

      if (drawing.tool === 'vline') {
        const t = Number((drawing.anchors as any).t);
        const xLine = projectors.timeToX(t);
        if (xLine == null) continue;
        const label = String(style.label || this.formatVlineLabel(t));
        this.ctx.save();
        this.ctx.font = '12px sans-serif';
        const metrics = this.ctx.measureText(label);
        this.ctx.restore();
        const { bx, by, boxW, boxH } = this.getVLineLabelBox(projectors, xLine, metrics.width);
        if (x >= bx && x <= bx + boxW && y >= by && y <= by + boxH) return drawing.id;
      }
    }
    return null;
  }

  private getHLineBoxes(
    projectors: ReturnType<DrawingManager['projectors']>,
    yLine: number,
    labelWidth: number,
    priceWidth: number,
  ) {
    const padding = 8;
    const boxH = 22;
    const rightInset = 4;
    const axisWidth = Math.max(40, projectors.axisRightWidth ?? 40);
    const axisLeft = projectors.width - axisWidth;
    const priceBoxW = Math.max(priceWidth + padding * 2, axisWidth - 2);
    const priceBx = Math.max(axisLeft, projectors.width - priceBoxW - rightInset);
    const by = Math.max(4, Math.min(projectors.height - boxH - 4, yLine - boxH / 2));
    const hasLabel = labelWidth > 0;
    const labelBoxW = hasLabel ? labelWidth + padding * 2 : 0;
    const labelBx = hasLabel ? Math.max(4, priceBx - labelBoxW) : priceBx;
    return {
      by,
      boxH,
      hasLabel,
      label: { bx: labelBx, boxW: labelBoxW },
      price: { bx: priceBx, boxW: priceBoxW },
    };
  }

  private getVLineLabelBox(
    projectors: ReturnType<DrawingManager['projectors']>,
    xLine: number,
    textWidth: number,
  ) {
    const padding = 8;
    const boxW = textWidth + padding * 2;
    const boxH = 22;
    const unclampedX = xLine - boxW / 2;
    const bx = Math.max(4, Math.min(projectors.width - boxW - 4, unclampedX));
    const by = 8;
    return { bx, by, boxW, boxH };
  }

  private formatVlineLabel(tSec: number): string {
    const ms = Number(tSec) * 1000;
    if (!Number.isFinite(ms)) return 'VLine';
    const dt = new Date(ms);
    if (Number.isNaN(dt.getTime())) return 'VLine';
    const month = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hh}:${mm}`;
  }

  // newer React-based modal version, invoked by tool handlers
  private async openLineEditDialogReact(initial: {
    label: string;
    lineColor: string;
    lineStyle: 'solid' | 'dashed' | 'dotted';
    lineWidth: number;
    showLabel?: boolean;
    showPrice?: boolean;
    price?: number;
  }): Promise<{ label: string; lineColor: string; lineStyle: 'solid' | 'dashed' | 'dotted'; lineWidth: number; price?: number } | null> {
    if (this.editingOverlay) {
      try { this.editingOverlay.remove(); } catch {}
      this.editingOverlay = null;
    }

    return await new Promise((resolve) => {
      const overlay = document.createElement('div');
      document.body.appendChild(overlay);
      this.editingOverlay = overlay;
      const root = createRoot(overlay);
      const cleanup = () => {
        try { root.unmount(); } catch {}
        try { overlay.remove(); } catch {}
        if (this.editingOverlay === overlay) this.editingOverlay = null;
      };
      const handleSave = (data: EditLineData) => {
        cleanup();
        resolve({ label: data.label, lineColor: data.color, lineStyle: data.style, lineWidth: data.thickness, price: data.price });
      };
      const handleCancel = () => {
        cleanup();
        resolve(null);
      };
      root.render(
        React.createElement(EditLineModal, {
          initial: { label: initial.label, style: initial.lineStyle, thickness: initial.lineWidth, color: initial.lineColor, price: initial.price },
          onSave: handleSave,
          onCancel: handleCancel,
          showLabel: initial.showLabel ?? true,
          showPrice: initial.showPrice ?? false,
        }),
      );
    });
  }
}
